import React, { Fragment } from 'react';
import Text from '../../Text/Text.js';
import { Key } from './Key.js';
import { accessibleKeyName } from '../key-names.js';

/**
 * Consistent sort order for modifier keys. There should never be more than one non-modifier
 * key in a chord, so we don't need to worry about sorting those - we just put them at
 * the end.
 */
const keySortPriorities = {
  control: 1,
  meta: 2,
  alt: 3,
  option: 4,
  shift: 5,
  function: 6
};
const keySortPriority = priority => {
  var _keySortPriorities$pr;
  return (_keySortPriorities$pr = keySortPriorities[priority]) !== null && _keySortPriorities$pr !== void 0 ? _keySortPriorities$pr : Infinity;
};
const compareLowercaseKeys = (a, b) => keySortPriority(a) - keySortPriority(b);

/** Split and sort the chord keys in standard order. */
const splitChord = chord => chord.split('+').map(k => k.toLowerCase()).sort(compareLowercaseKeys);
const Chord = ({
  keys,
  format = 'condensed',
  variant = 'normal'
}) => /*#__PURE__*/React.createElement(Text, {
  sx: {
    display: 'inline-flex',
    bg: 'transparent',
    color: variant === 'onEmphasis' ? 'fg.onEmphasis' : 'fg.muted',
    border: '1px solid',
    borderColor: 'border.default',
    borderRadius: 2,
    fontWeight: 'normal',
    fontFamily: 'normal',
    fontSize: 0,
    p: 1,
    gap: '0.5ch',
    boxShadow: 'none',
    verticalAlign: 'baseline',
    overflow: 'hidden',
    lineHeight: '10px'
  }
}, splitChord(keys).map((k, i) => /*#__PURE__*/React.createElement(Fragment, {
  key: i
}, i > 0 && format === 'full' ? /*#__PURE__*/React.createElement("span", {
  "aria-hidden": true
}, " + ") // hiding the plus sign helps screen readers be more concise
: ' ' // space is nonvisual due to flex layout but critical for labelling / screen readers
, /*#__PURE__*/React.createElement(Key, {
  name: k,
  format: format
}))));
Chord.displayName = "Chord";
/** Plain string version of `Chord` for use in `aria` string attributes. */
const accessibleChordString = (chord, isMacOS) => splitChord(chord).map(key => accessibleKeyName(key, isMacOS)).join(' ');

export { Chord, accessibleChordString };
